// contexts/DialogContext.tsx
import React, { createContext, useContext, useState, ReactNode } from 'react';
import ConfirmationDialog from '../components/dialog/ConfirmationDialog';
import { setFormClean } from 'store/reducers/formslice';
import { useDispatch } from 'react-redux';

interface DialogContextType {
  showDialog: (config: { actionType?: string; confirmationKey?: string; onConfirm?: () => void; onClose?: () => void }) => void;
}

const DialogContext = createContext<DialogContextType | undefined>(undefined);

export const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context;
};

export const DialogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [dialogConfig, setDialogConfig] = useState<{
    open: boolean;
    actionType?: string;
    confirmationKey?: string;
    onConfirm?: () => void;
    onClose?: () => void;
  }>({
    open: false
  });
  const dispatch = useDispatch();
  const showDialog = (config: { actionType?: string; confirmationKey?: string; onConfirm?: () => void; onClose?: () => void }) => {
    setDialogConfig({ open: true, ...config });
  };

  const handleClose = () => {
    if (dialogConfig.onClose) dialogConfig.onClose();
    setDialogConfig({ open: false });
  };

  const handleConfirm = () => {
    if (dialogConfig.onConfirm) dialogConfig.onConfirm();
    setDialogConfig({ open: false });
    dispatch(setFormClean());
  };

  return (
    <DialogContext.Provider value={{ showDialog }}>
      {children}
      {dialogConfig.open && (
        <ConfirmationDialog
          open={dialogConfig.open}
          onClose={handleClose}
          onConfirm={handleConfirm}
          isLoading={false}
          confirmationKey={dialogConfig.confirmationKey}
          alertMessage="You have unsaved changes that will be lost if you proceed"
          message="Are you sure you want to leave this page?"
          confirmButtonLabel="Leave this Page without saving"
          cancelButtonLabel="Stay on this page"
          type="unsavedChanges"
          actionType="unsavedChanges"
        />
      )}
    </DialogContext.Provider>
  );
};
